// src/components/ManageAssessments.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';          
import './ManageAssessments.css';   
import CreateAssessmentPopup from './CreateAssessmentPopup';
import EditAssessmentPopup from './EditAssessmentPopup';
import Snackbar from './Snackbar';
import GlobalHeader from './GlobalHeader';
import BulkUploadResultPopup from './BulkUploadResultPopup';

const logPayload = (payload) => {
  console.groupCollapsed('%c Edit-Assessment Payload', 'font-weight:bold;color:#1a73e8');
  console.log('Assessment ID  :', payload.id);
  console.log('Department      :', payload.department);
  console.log('Role            :', payload.role);
  console.log('Created By      :', payload.created_by);
  console.table(
    payload.questions.map((q, i) => ({
      '#': i + 1,
      ID: q.ID ?? '(new)',
      CategoryID: q.CategoryID,
      Question: q.Question,
      Weightage: q.Weightage,
    }))
  );
  console.groupEnd();
};

function ManageAssessments() {
  const navigate = useNavigate();

  // Page title
  useEffect(() => {
    document.title = 'CR Capability Index - Manage Assessments';
  }, []);

 
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [departmentFilter, setDepartmentFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [assessments, setAssessments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [departmentsMetadata, setDepartmentsMetadata] = useState(null);
  const [rolesMetadata,       setRolesMetadata]       = useState(null);
  const [categoriesMetadata,  setCategoriesMetadata]  = useState(null);
  const [isMetadataLoading, setIsMetadataLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingAssessment, setDeletingAssessment] = useState(null);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [bulkUploadPopupOpen, setBulkUploadPopupOpen] = useState(false);
  const [guidelinesModalOpen, setGuidelinesModalOpen] = useState(false);
  const [guidelines, setGuidelines] = useState([]);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);
  const [selectedGuidelinesDept, setSelectedGuidelinesDept] = useState('');
  const [expandedFamilies, setExpandedFamilies] = useState(new Set());
  const [snackbar, setSnackbar] = useState({ message: '', type: 'success', isOpen: false });
  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ message, type, isOpen: true });
  };

  const userRole = (sessionStorage.getItem('access_role') || '').toLowerCase();
const isAdmin = userRole === 'admin';

const [sortOrder, setSortOrder] = useState('desc');
const managerDept = sessionStorage.getItem('user_department')?.trim();  
 useEffect(() => {
  setCurrentPage(1);
}, [searchTerm, departmentFilter, statusFilter]);

const fetchAssessments = async () => {
  try {
    setLoading(true);
    setError(null);

    const userRole = (sessionStorage.getItem('access_role') || '').toLowerCase();
    const isAdmin = userRole === 'admin';
    const managerEmail = sessionStorage.getItem('user_emailId') || '';

    // Use the same endpoint for both admin and manager
    const endpoint = 'manager-role-questions?is_admin=true';
    console.log('Fetching assessments from:', endpoint);

    const data = await apiService.fetchData(endpoint);

    if (data.status !== 'success') {
      throw new Error(data.message || 'API returned unsuccessful status');
    }

    const roleMap = new Map();

    // Helper function to extract questions and metadata from a single role
    // const extractRoleData = (roleObj, dept) => {
    //   const allQuestions = [];
    //   let earliestDate = '';
    //   let createdBy = 'Unknown';

    //   roleObj.categories.forEach(cat => {
    //     cat.questions.forEach(q => {
    //       const questionWithCategory = {
    //         ...q,
    //         CategoryID: cat.category_id,
    //         CategoryName: cat.category_name,
    //       };
    //       allQuestions.push(questionWithCategory);

    //       const qDate = q.CreatedAt.split(' ')[0];
    //       if (!earliestDate || qDate < earliestDate) {
    //         earliestDate = qDate;
    //         createdBy = q.CreatedByManagerEmail || 'Unknown';
    //       }
    //     });
    //   });

    //   return { allQuestions, earliestDate, createdBy };
    // };
   const processQuestion = (q, cat, state) => {
  const questionWithCategory = {
    ...q,
    CategoryID: cat.category_id,
    CategoryName: cat.category_name,
    CategoryDescription: cat.category_description,
  };

  state.allQuestions.push(questionWithCategory);

  const qDate = q.CreatedAt.split(' ')[0];
  if (!state.earliestDate || qDate < state.earliestDate) {
    state.earliestDate = qDate;
    state.createdBy = q.CreatedByManagerEmail || state.createdBy;
  }
};
const extractRoleData = (roleObj, dept) => {
  const state = {
    allQuestions: [],
    earliestDate: '',
    createdBy: 'Unknown',
  };

  roleObj.categories.forEach(cat => {
    cat.questions.forEach(q => {
      processQuestion(q, cat, state);
    });
  });

  return state;
};


    // For manager: Filter to only their department
    let filteredData = data.data;
    if (!isAdmin && managerDept) {
      filteredData = data.data.filter(d => d.department_name === managerDept);
      console.log('Filtered for manager department:', managerDept, '→', filteredData.length, 'departments found');
    }

    filteredData.forEach(dept => {
      dept.roles.forEach(roleObj => {
        const key = `${dept.department_name}|${roleObj.role}`;

        if (!roleMap.has(key)) {
          const { allQuestions, earliestDate, createdBy } = extractRoleData(roleObj, dept);

          if (allQuestions.length > 0) {
            roleMap.set(key, {
              id: allQuestions[0].ID,
              name: roleObj.role,
              roleFamily: roleObj.role_family || 'Uncategorized',
              department: dept.department_name,
              createdBy,
              createdOn: earliestDate,
              status: 'Published',
              questions: allQuestions,
              levelOrder: roleObj.level_order || 0,
            });
          }
        }
      });
    });

    // Convert to array and sort by creation date (newest first)
    let flat = Array.from(roleMap.values());
    flat = flat.sort((a, b) => b.createdOn.localeCompare(a.createdOn));

    setAssessments(flat);

    // Extract unique departments for filter dropdown (Admin only)
    // const uniqDepts = [...new Set(flat.map(a => a.department))].sort();
    const uniqDepts = [...new Set(flat.map(a => a.department))]
  .sort((a, b) => a.localeCompare(b));

    setDepartments(uniqDepts);

  } catch (err) {
    console.error('API Error:', err);
    setError(err.message || 'Failed to load assessments. Please try again.');
  } finally {
    setLoading(false);
  }
};
useEffect(() => {
  fetchAssessments();
}, []);


  const filteredAssessments = React.useMemo(() => {
  // Step 1: Apply search + status + department filters
  let result = assessments.filter(assessment => {
    const matchesSearch =
      assessment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.createdBy.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assessment.status.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'All' || assessment.status === statusFilter;
    const matchesDept = !isAdmin || departmentFilter === 'All' || assessment.department === departmentFilter;

    return matchesSearch && matchesStatus && matchesDept;
  });

  // Step 2: Sort by Created On date (DD-MM-YYYY format)
  result = [...result].sort((a, b) => {
    // Convert "25-03-2025" → valid Date object
    const [dayA, monthA, yearA] = a.createdOn.split('-').map(Number);
    const [dayB, monthB, yearB] = b.createdOn.split('-').map(Number);

    const dateA = new Date(yearA, monthA - 1, dayA);
    const dateB = new Date(yearB, monthB - 1, dayB);

    return sortOrder === 'desc' 
      ? dateB - dateA   // Newest first
      : dateA - dateB;  // Oldest first
  });

  return result;
}, [assessments, searchTerm, statusFilter, departmentFilter, isAdmin, sortOrder]);

// Group by role family first, then paginate
const assessmentsByFamily = filteredAssessments.reduce((acc, assessment) => {
  const family = assessment.roleFamily || 'Uncategorized';
  const dept = assessment.department;
  const key = `${dept}|${family}`; // Group by department AND family
  if (!acc[key]) acc[key] = [];
  acc[key].push(assessment);
  return acc;
}, {});

// Sort families alphabetically and sort roles within each family by level_order
const sortedFamilies = Object.keys(assessmentsByFamily).sort();
sortedFamilies.forEach(key => {
  assessmentsByFamily[key].sort((a, b) => (a.levelOrder || 0) - (b.levelOrder || 0));
});

// Paginate families instead of individual assessments
const itemsPerPage = 10;
const startIndex = (currentPage - 1) * itemsPerPage;
const paginatedFamilies = sortedFamilies.slice(startIndex, startIndex + itemsPerPage);

  // Status badge class
  const getStatusClass = (status) => {
    switch (status) {
      case 'Draft': return 'status-draft';
      case 'Published': return 'status-published';
      case 'Archived': return 'status-archived';
      default: return '';
    }
  };
const handleClearFilters = () => {
  setSearchTerm('');           
  setStatusFilter('All');      
  setDepartmentFilter('All'); 
  setCurrentPage(1);          
};



// Helper: Convert department name → department_id
// If departmentName is provided, use it; otherwise fall back to user's department
const getDepartmentId = async (departmentName = null) => {
  const deptName = departmentName?.trim() || sessionStorage.getItem('user_department')?.trim();
  if (!deptName) return null;

  try {
    const deptData = await apiService.fetchData('metadata?type=departments');
    const matchedDept = deptData.data.find(
      d => d.DepartmentName.trim().toLowerCase() === deptName.toLowerCase()
    );
    return matchedDept ? matchedDept.DepartmentID : null;
  } catch (err) {
    console.error('Failed to fetch department ID', err);
    return null;
  }
};

  const handleView = (assessment) => {
  const grouped = {};
  assessment.questions.forEach(q => {
    const catId = q.CategoryID;
    const catName = q.CategoryName || `Category ${catId}`;
    if (!grouped[catId]) {
      grouped[catId] = { name: catName, description: q.CategoryDescription, questions: [] };
    }
    grouped[catId].questions.push(q);
  });

  setSelectedRole({
    role: assessment.name,
    department: assessment.department,
    groupedQuestions: grouped,
  });
  setModalOpen(true);
};

const handleEdit = async (assessment) => {
  if (!assessment) {
    showSnackbar("Cannot edit: assessment data missing", 'error');
    return;
  }

  setIsMetadataLoading(true);
  setEditingAssessment(assessment);
  setEditModalOpen(true);

  try {
    // Use the assessment's department, not the logged-in user's department
    const departmentId = await getDepartmentId(assessment.department);
    if (!departmentId) {
      showSnackbar('Could not determine the assessment department.', 'error');
      setEditModalOpen(false);
      setIsMetadataLoading(false);
      return;
    }

    const [deptData, roleData, catData] = await Promise.all([
      apiService.fetchData('metadata?type=departments'),
      apiService.fetchData(`metadata?type=roles&department_id=${departmentId}`),
      apiService.fetchData(`metadata?type=categories&department_id=${departmentId}`)
    ]);

    setDepartmentsMetadata(deptData);
    setRolesMetadata(roleData);
    setCategoriesMetadata(catData);
  } catch (err) {
    console.error('Failed to load metadata', err);
    showSnackbar('Failed to load form data. Please try again.', 'error');
    setEditModalOpen(false);
  } finally {
    setIsMetadataLoading(false);
  }
};

// ============================================================
// UNIFIED BULK UPLOAD - Single sheet for Roles + Categories + Questions
// This replaces the old separate questions upload
// ============================================================
const handleDownloadUnifiedTemplate = async () => {
  try {
    const userRole = sessionStorage.getItem('access_role') || '';
    const userDepartment = sessionStorage.getItem('user_department');
    
    // Determine which department to use
    let department = null;
    if (isAdmin) {
      if (departmentFilter && departmentFilter !== 'All') {
        department = departmentFilter;
      }
    } else {
      department = userDepartment;
      if (!department) {
        showSnackbar('Department not found in session', 'error');
        return;
      }
    }

    let url = `/bulk-upload/unified-template?user_role=${encodeURIComponent(userRole)}`;
    if (department) {
      url += `&department_name=${encodeURIComponent(department)}`;
    }

    const response = await apiService.fetchData(url, {
      responseType: 'blob'
    });
    
    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    
    let filename;
    if (isAdmin && (!departmentFilter || departmentFilter === 'All')) {
      filename = 'unified_template_all_departments.xlsx';
    } else {
      const deptName = department || userDepartment;
      filename = `unified_template_${deptName.replace(/ /g, '_')}.xlsx`;
    }
    link.download = filename;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
    showSnackbar('Unified template downloaded successfully!', 'success');
  } catch (error) {
    console.error('Failed to download unified template:', error);
    showSnackbar('Failed to download unified template', 'error');
  }
};

const handleUnifiedBulkUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  
  const formData = new FormData();
  formData.append('file', file);
  
  const userRole = sessionStorage.getItem('access_role') || '';
  const userDepartment = sessionStorage.getItem('user_department');
  const managerEmail = sessionStorage.getItem('user_emailId');
  
  // For admin: use selected department filter, or leave empty for all departments
  // For manager: use their own department
  let department = null;
  if (isAdmin) {
    if (departmentFilter && departmentFilter !== 'All') {
      department = departmentFilter;
    }
    // If 'All' selected, department is null - backend handles all departments from Excel
  } else {
    department = userDepartment;
  }
  
  if (department) formData.append('department_name', department);
  if (managerEmail) formData.append('manager_email', managerEmail);
  formData.append('user_role', userRole);
  
  try {
    setSnackbar({ 
      message: 'Processing upload... This will create/update roles, categories, and questions.', 
      type: 'info', 
      isOpen: true,
      duration: 1800000  // 30 minutes
    });
    
    const response = await apiService.postBulkUpload('/bulk-upload/unified', formData);
    
    setSnackbar(prev => ({ ...prev, isOpen: false }));
    
    if (response && (response.status === 'success' || response.status === 'partial_success' || response.status === 'error')) {
      setBulkUploadResult(response);
      setBulkUploadPopupOpen(true);
      
      if (response.status === 'success' || response.status === 'partial_success') {
        await fetchAssessments();
      }
    } else {
      showSnackbar('Upload completed but response format unexpected', 'warning');
    }
  } catch (error) {
    console.error('Unified bulk upload failed:', error);
    setSnackbar(prev => ({ ...prev, isOpen: false }));
    showSnackbar('Upload failed: ' + (error.message || 'Unknown error'), 'error');
  } finally {
    event.target.value = '';
  }
};

const fetchGuidelines = async (departmentName = null) => {
  try {
    setGuidelinesLoading(true);
    const deptName = departmentName || (isAdmin ? selectedGuidelinesDept : managerDept);
    
    if (!deptName) {
      setGuidelines(getDefaultGuidelines());
      return;
    }

    const response = await apiService.fetchData(`guidelines?department_name=${encodeURIComponent(deptName)}`);
    
    if (response.status === 'success') {
      if (response.data && response.data.length > 0) {
        setGuidelines(response.data);
      } else {
        if (isAdmin && departmentName) {
          setGuidelines([]);
        } else {
          setGuidelines(getDefaultGuidelines());
        }
      }
    } else {
      setGuidelines(getDefaultGuidelines());
    }
  } catch (error) {
    console.error('Failed to fetch guidelines:', error);
    setGuidelines(getDefaultGuidelines());
  } finally {
    setGuidelinesLoading(false);
  }
};

const getDefaultGuidelines = () => {
  return [
    { sno: 1, section: 'Purpose', desc: 'The Capability Index enables every Engineer to assess their current technical and professional proficiency against organization-wide standards for Junior, Senior, Lead, and Architect levels. It supports personal growth, identifies upskilling needs, and aligns capability maturity across teams.' },
    { sno: 2, section: 'Scope', desc: 'The assessment covers all critical areas for a modern Engineer — Frameworks, Architecture, Tooling, Cloud, AI-augmented development, and Collaboration.' },
    { sno: 3, section: 'Assessment Approach', desc: 'Review each capability area in the main grid. Rate yourself on a scale of 1–5 based on your demonstrated proficiency. Each capability has a weight (%) indicating its importance toward your total score.' },
    { sno: 4, section: 'Rating Scale Definition', desc: '1 – Not Aware: You are unfamiliar with the concept or have not used it in practice.\n2 – Basic Awareness / Supervised Practice: You understand fundamentals and can perform tasks under guidance.\n3 – Independent Proficiency: You can independently apply the skill in real projects with minimal assistance.\n4 – Advanced / Mentor Level: You mentor others, optimize processes, and are a subject-matter reference for the team.\n5 – Expert / Strategic Leader: You define standards, frameworks, or strategies for this capability across teams or the organization.' },
    { sno: 5, section: 'How to Rate', desc: 'Rate your current, demonstrated ability — not your aspiration or one-time exposure. Base your rating on actual project examples or measurable experience.' },
    { sno: 6, section: 'Weightage', desc: 'Each capability contributes to the total score according to its assigned weight. Total of all weights = 100%. Higher weight indicates higher importance in overall capability maturity.' },
    { sno: 7, section: 'Final Score Calculation', desc: 'Your Final Capability Score (out of 5) = Σ (Rating × Weight%) ÷ 100.' },
    { sno: 8, section: 'Example Calculation', desc: 'Example: Frameworks (12%) × Rating 4 = 0.48; Testing (8%) × Rating 3 = 0.24; AI-Augmented Dev (8%) × Rating 2 = 0.16 → Total Score = 3.6 / 5 (≈72%).' },
    { sno: 10, section: 'Evidence-Based Evaluation', desc: 'When scoring yourself, refer to:\n• Real projects or deliverables\n• Code contributions or reviews\n• Mentorship or leadership examples\n• Measurable technical outcomes (performance, automation, design systems)' },
    { sno: 11, section: 'Best Practices for Self-Assessment', desc: '1️⃣ Be objective — rate based on consistent, repeatable performance.\n2️⃣ Use evidence — reference work or contributions.\n3️⃣ Don\'t underrate — acknowledge demonstrated capability.\n4️⃣ Seek calibration from your lead/manager.\n5️⃣ Repeat assessment every 6 months to measure progress.' },
    { sno: 12, section: 'Outcome of the Assessment', desc: 'You\'ll obtain your weighted capability index (out of 5 and %). The results help identify strengths, improvement areas, and readiness for progression (Junior → Senior → Lead → Architect).' },
    { sno: 13, section: 'Next Steps', desc: 'After completing your self-rating:\n• Review results with your Manager.\n• Identify focus areas for improvement.\n• Define a learning plan or mentorship goal for the next review cycle.' }
  ];
};

const confirmDelete = async () => {
  if (!deletingAssessment) return;

  try {
    // CORRECT ENDPOINT: Use query parameter ?role_name=
    const roleName = deletingAssessment.name; // e.g., "Director – Global Learning & Development"
    const endpoint = `manager-role-questions?role_name=${encodeURIComponent(roleName)}`;

    console.log('Deleting with URL:', endpoint);
    // Will log: manager-role-questions?role_name=Director%20%E2%80%93%20Global%20Learning%20%26%20Development
    // This is CORRECT — %E2%80%93 is the proper encoding of "–"

    const result = await apiService.deleteData(endpoint);

    if (result.status === 'success') {
      showSnackbar('Assessment deleted successfully!', 'success');
      await fetchAssessments(); // refresh the list
    } else {
      throw new Error(result.message || 'Delete failed');
    }
  } catch (err) {
    console.error('Delete error:', err);
    // Show user-friendly message
    if (err.message.includes('Failed to fetch') || err.message.includes('ERR_CONNECTION_REFUSED')) {
      showSnackbar('Cannot connect to server. Is the backend running?', 'error');
    } else {
      showSnackbar(err.message || 'Failed to delete assessment', 'error');
    }
  } finally {
    setDeleteModalOpen(false);
    setDeletingAssessment(null);
  }
};

const closeEditModal = () => {
  setEditModalOpen(false);
  setEditingAssessment(null);
};


const handleEditSave = async (payload) => {
  try {
    const managerEmail = sessionStorage.getItem('user_emailId') || '';
    const baseEndpoint = `manager-role-questions`;

    // 1. DELETE removed questions
    const deletePromises = (payload.deleted_questions || []).map(async (id) => {
      const endpoint = `${baseEndpoint}/${id}?manager_email=${encodeURIComponent(managerEmail)}`;
      return apiService.deleteData(endpoint);
    });

    // 2. POST new questions
    const newQuestions = payload.questions.filter(q => !q.ID);
    if (newQuestions.length > 0) {
      const postPayload = {
        department: payload.department,
        role: payload.role,
        created_by: payload.created_by,
        questions: newQuestions.map(q => ({
          CategoryID: q.CategoryID,
          Question: q.Question,
          Weightage: q.Weightage
        }))
      };
      const postEndpoint = `${baseEndpoint}?manager_email=${encodeURIComponent(managerEmail)}`;
      await apiService.postData(postEndpoint, postPayload);
    }

    // 3. PUT updated questions
    const updatePromises = payload.questions
      .filter(q => q.ID)
      .map(async (q) => {
        const body = {};
        if (q.Question) body.Question = q.Question.trim();
        if (q.Description !== undefined) body.Description = q.Description || '';
        if (q.Weightage !== undefined) body.Weightage = q.Weightage;
        if (q.CategoryID) body.CategoryID = q.CategoryID;

        const endpoint = `${baseEndpoint}/${q.ID}?manager_email=${encodeURIComponent(managerEmail)}`;
        return apiService.putData(endpoint, body);
      });

    await Promise.all([...deletePromises, ...updatePromises]);

    showSnackbar('Assessment updated successfully!', 'success');
    await new Promise(r => setTimeout(r, 300));
    closeEditModal();
    await fetchAssessments(); // Refresh list

  } catch (err) {
    console.error('Save error:', err);
    showSnackbar('Failed to update.', 'error');
  }
};



 const handleDelete = (assessment) => {
  setDeletingAssessment(assessment);
  setDeleteModalOpen(true);
};
const handleDeleteClick = (assessment) => {
  setDeletingAssessment(assessment);
  setDeleteModalOpen(true);
};

 
  if (loading) {
  return (
    <div className="manage-assessments-container">
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        backgroundColor: '#ffffff'
      }}>
        {/* Exact Dark-Grey Stylish Arc Loader (as in your image) */}
        <div style={{
          width: '56px',
          height: '56px',
          border: '4px solid #e2e8f0',
          borderTop: '4px solid #64748b',   /* Dark grey - matches your image */
          borderRadius: '50%',
          animation: 'spin 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite',
          boxSizing: 'border-box'
        }}></div>

        {/* Subtle Professional Text */}
        <p style={{
          marginTop: '28px',
          fontSize: '17px',
          fontWeight: '500',
          color: '#64748b',
          letterSpacing: '-0.2px'
        }}>
          Loading assessments...
        </p>

        {/* Smooth Animation */}
        <style jsx>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
}

  if (error) {
    return (
      <div className="manage-assessments-container">
        <div style={{ padding: '2rem', color: 'red', textAlign: 'center' }}>{error}</div>
      </div>
    );
  }

 
  return (
    <div className="manage-assessments-container">
      <GlobalHeader />
      <div className="ma-page-header">
  <button className="ma-back-button" onClick={() => navigate('/Home')}>
    <span className="back-icon">←</span> Back to Home
  </button>

  {/* Title + subtitle */}
  <div className="ma-header-content">
    <div className="ma-heading">
    <h1 className="page-title">Manage Assessments</h1>
    <p className="page-subtitle">
      View, edit, and delete assessment records (Admin & Manager access)
    </p>
    </div>
    <div className="ma-ca-button">
      {/* View Guidelines Button */}
      <button
        onClick={async () => {
          setGuidelinesModalOpen(true);
          if (isAdmin && !selectedGuidelinesDept) {
            setSelectedGuidelinesDept(departmentFilter !== 'All' ? departmentFilter : (departments[0] || ''));
          }
          await fetchGuidelines();
        }}
        className="btn-view-guidelines"
        style={{ 
          padding: '12px 20px',
          backgroundColor: '#6366f1',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer',
          transition: 'background 0.2s',
          whiteSpace: 'nowrap',
          marginRight: '10px',
          marginTop: '60px'
        }}
        title="View Assessment Guidelines"
      >
        📖 View Guidelines
      </button>
      
      {/* UNIFIED Single-Sheet Bulk Upload - Download & Upload */}
      <button
        onClick={handleDownloadUnifiedTemplate}
        className="btn-download-template"
        style={{ 
          marginRight: '10px', 
          marginTop: '60px',
          backgroundColor: '#059669',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 20px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer'
        }}
        title="Download single-sheet template with roles, categories & questions"
      >
        📥 Download Template
      </button>
      <button
        onClick={() => document.getElementById('unified-bulk-upload').click()}
        className="btn-bulk-upload"
        style={{ 
          marginRight: '15px', 
          marginTop: '60px',
          backgroundColor: '#0891b2',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 20px',
          fontSize: '14px',
          fontWeight: '700',
          cursor: 'pointer'
        }}
        title="Upload single sheet to create/update roles, categories & questions at once"
      >
        📤 Bulk Upload 
      </button>
      <input
        id="unified-bulk-upload"
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleUnifiedBulkUpload}
      />
      
       <button
    className="create-assessment-btn"
    onClick={async () => {
  setIsMetadataLoading(true);
  setCreateModalOpen(true);

  try {
    const departmentId = await getDepartmentId();
    if (!departmentId) {
      showSnackbar('Could not determine your department.', 'error');
      setIsMetadataLoading(false);
      return;
    }

    const [deptData, roleData, catData] = await Promise.all([
      apiService.fetchData('metadata?type=departments'),
      apiService.fetchData(`metadata?type=roles&department_id=${departmentId}`),
      apiService.fetchData(`metadata?type=categories&department_id=${departmentId}`)
    ]);

    setDepartmentsMetadata(deptData);
    setRolesMetadata(roleData);
    setCategoriesMetadata(catData);
  } catch (err) {
    console.error('Failed to load metadata', err);
    showSnackbar('Failed to load form data. Please try again.', 'error');
  } finally {
    setIsMetadataLoading(false);
  }
}}
  >
    <span className="plus-icon">+</span> New Assessment
  </button>
    </div>
  </div>

  {/* Create button - now with plus icon */}
 
</div>

      {/* Controls */}
      <div className="ma-controls-section">
        <div className="search-container">
          <div className="search-input">
            <input
              type="text"
              placeholder="Search by name, status, etc."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

       
        <div className="filter-container">
  
  {isAdmin && (
    <select
      value={departmentFilter}
      onChange={(e) => setDepartmentFilter(e.target.value)}
      className="status-filter"
      style={{ marginLeft: '8px' }}
    >
      <option value="All">All Departments</option>
      {departments.map(d => (
        <option key={d} value={d}>{d}</option>
      ))}
    </select>
  )}

  {/* Clear Filters Button – placed right after the dropdowns */}
  <button
    className="clear-filters-btn"
    onClick={handleClearFilters}
    title="Clear all filters"
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ marginRight: '6px' }}
    >
      <path d="M19.07 4.93A10 10 0 0 0 2.93 21.07 10 10 0 1 0 21.07 2.93Z" />
      <path d="M15 9l-6 6" />
      <path d="M9 9l6 6" />
    </svg>
    Clear Filters
  </button>
</div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="assessments-table">
          <thead style={{
            background: "linear-gradient(90deg, rgb(59, 130, 246) 0%, rgb(124, 58, 237) 50%, rgb(168, 85, 247) 100%)",
            color: "white",
            textAlign: 'center'
          }}>
            <tr>
              <th></th>
              <th>Role Family</th>
              <th>Practice Name</th>
              <th>Roles</th>
              <th>Questions</th>
              <th>Created By</th>
              <th
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
              >
                Created On{' '}
                <span style={{ marginLeft: '6px', fontSize: '14px' }}>
                  {sortOrder === 'desc' ? '🔽' : '🔼'}
                </span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const itemsPerPage = 10;
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = startIndex + itemsPerPage;
              const paginatedAssessments = paginatedFamilies.flatMap(familyName => assessmentsByFamily[familyName]);

                if (paginatedAssessments.length === 0 && filteredAssessments.length > 0) {
                  return (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                        No assessments on this page.
                      </td>
                    </tr>
                  );
                }

              return paginatedAssessments.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                    No assessments found.
                  </td>
                </tr>
              ) : (
                (() => {
                  const toggleFamily = (key) => {
                    const newExpanded = new Set(expandedFamilies);
                    if (newExpanded.has(key)) {
                      newExpanded.delete(key);
                    } else {
                      newExpanded.add(key);
                    }
                    setExpandedFamilies(newExpanded);
                  };

                  return paginatedFamilies.map(key => {
                    const assessments = assessmentsByFamily[key];
                    const [dept, familyName] = key.split('|');
                    const totalQuestions = assessments.reduce((sum, a) => sum + a.questions.length, 0);
                    const firstAssessment = assessments[0];
                    const isExpanded = expandedFamilies.has(key);
                    
                    return [
                      // Role Family Header Row - Always show summary
                      <tr key={`family-${key}`} style={{ cursor: 'pointer', backgroundColor: '#f8fafc' }} onClick={() => toggleFamily(key)}>
                        <td style={{ textAlign: 'center', width: '40px' }}>
                          <span style={{ fontSize: '18px', color: '#64748b' }}>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </td>
                        <td style={{ fontWeight: 'bold', color: '#1e40af', padding: '12px' }}>{familyName}</td>
                        <td style={{ padding: '12px' }}>{dept}</td>
                        <td style={{ padding: '12px' }}>{assessments.length} role{assessments.length !== 1 ? 's' : ''}</td>
                        <td style={{ padding: '12px' }}>{totalQuestions} question{totalQuestions !== 1 ? 's' : ''}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>,
                      // Individual role rows (only if expanded)
                      ...(isExpanded ? assessments.map((assessment) => (
                        <tr key={assessment.id} style={{ backgroundColor: '#ffffff', borderLeft: '3px solid #e5e7eb' }}>
                          <td></td>
                          <td style={{ paddingLeft: '40px' }}>
                            {assessment.name}
                          </td>
                          <td></td>
                          <td></td>
                          <td>{assessment.questions.length} question{assessment.questions.length > 1 ? 's' : ''}</td>
                          <td style={{ color: "#1882b0ff" }}>{assessment.createdBy}</td>
                          <td>{assessment.createdOn}</td>
                          <td>
                            <div className="ma-action-buttons">
                              <button
                                className="ma-action-btn view-btn"
                                onClick={() => handleView(assessment)}
                                title="View"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                              <button
                                className="ma-action-btn edit-btn"
                                onClick={() => handleEdit(assessment)}
                                title="Edit"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5l3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                              <button
                                className="ma-action-btn delete-btn"
                                onClick={() => handleDelete(assessment)}
                                title="Delete"
                              >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M3 6h18" />
                                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                  <path d="M19 6v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                                  <line x1="10" y1="11" x2="10" y2="17" />
                                  <line x1="14" y1="11" x2="14" y2="17" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      )) : [])
                    ];
                  }).flat();
                })()
            );
          })()}
          </tbody>
        </table>
      </div>


<div className="ma-pagination-section">
  <div className="pagination-info">
    Showing {sortedFamilies.length === 0 ? 0 : (currentPage - 1) * 10 + 1} to{' '}
    {Math.min(currentPage * 10, sortedFamilies.length)} of {sortedFamilies.length} entries
  </div>

  <div className="pagination-controls">
    <button
      className="pagination-btn"
      disabled={currentPage === 1}
      onClick={() => setCurrentPage(currentPage - 1)}
    >
      Previous
    </button>

    <div className="page-numbers">
      {(() => {
        const totalPages = Math.ceil(sortedFamilies.length / 10);
        const pages = [];

        for (let i = 1; i <= totalPages; i++) {
          pages.push(
            <button
              key={i}
              className={`page-number ${currentPage === i ? 'active' : ''}`}
              onClick={() => setCurrentPage(i)}
            >
              {i}
            </button>
          );
        }

        return pages.length > 0 ? pages : <span style={{ color: '#94a3b8', fontSize: '14px' }}>1</span>;
      })()}
    </div>

    <button
      className="pagination-btn"
      disabled={currentPage >= Math.ceil(sortedFamilies.length / 10)}
      onClick={() => setCurrentPage(currentPage + 1)}
    >
      Next
    </button>
  </div>
</div>

      {/* Modal */}
      <QuestionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={selectedRole}
      />
      
<CreateAssessmentPopup
  open={createModalOpen}
  onClose={() => setCreateModalOpen(false)}
  onSave={async (payload) => {
    try {
      console.log('Assessment payload:', payload);
      await fetchAssessments();
     showSnackbar('Assessment created successfully!', 'success');
      setCreateModalOpen(false);
    } catch (err) {
      showSnackbar('Failed to save.', 'error');
    }
  }}
  departmentsData={departmentsMetadata}      
  rolesData={rolesMetadata}                  
  categoriesMetadata={categoriesMetadata}   
  isMetadataLoading={isMetadataLoading}
  managerDepartment={managerDept}
/>
{/* ────────────────────── EDIT POPUP ────────────────────── */}
<EditAssessmentPopup
  open={editModalOpen}
  onClose={closeEditModal}
  onSave={handleEditSave}
  assessment={editingAssessment}
  departmentsData={departmentsMetadata}
  rolesData={rolesMetadata}
  categoriesMetadata={categoriesMetadata}
  isMetadataLoading={isMetadataLoading}
  managerDepartment={managerDept}
/>
 {snackbar.isOpen && (
  <Snackbar
    message={snackbar.message}
    type={snackbar.type}
    duration={snackbar.duration || 3000}
    onClose={() => setSnackbar(prev => ({ ...prev, isOpen: false }))}
  />
)}
{/* DELETE CONFIRMATION MODAL */}
{deleteModalOpen && (
  <div className="ma-delete-overlay" onClick={() => setDeleteModalOpen(false)}>
   <div className="ma-delete-modal" onClick={e => e.stopPropagation()}>
  <div style={{ textAlign: 'center', marginBottom: '24px' }}>
    <div style={{
      width: '80px',
      height: '80px',
      backgroundColor: '#fee2e2',
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px'
    }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="3">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    </div>
    <h3 style={{ fontSize: '22px', fontWeight: '700', color: '#1f2937', margin: '0 0 12px 0' }}>
      Delete Assessment
    </h3>
    <p style={{ fontSize: '16px', color: '#4b5563', margin: '0 0 24px 0', lineHeight: '1.6' }}>
      Are you sure you want to <strong>permanently delete</strong> the assessment for
      <br />
      <strong style={{ color: '#dc2626', fontSize: '18px' }}>{deletingAssessment?.name}</strong>
      <br />
      in <strong>{deletingAssessment?.department}</strong>?
    </p>
    <div style={{
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '12px',
      padding: '16px',
      margin: '20px 0',
      textAlign: 'left'
    }}>
      <p style={{ margin: 0, color: '#991b1b', fontWeight: '600', fontSize: '15px' }}>
        ⚠️ This action cannot be undone.
      </p>
      <p style={{ margin: '8px 0 0 0', color: '#7f1d1d', fontSize: '14px' }}>
        All questions and categories associated with this role will be permanently deleted.
      </p>
    </div>
  </div>

  <div className="ma-delete-actions">
    <button
      className="ma-delete-btn-cancel"
      onClick={() => setDeleteModalOpen(false)}
    >
      Cancel
    </button>
    <button
      className="ma-delete-btn-confirm"
      onClick={confirmDelete}
    >
      Yes, Delete Permanently
    </button>
  </div>
</div>
  </div>
)}

{/* Bulk Upload Result Popup */}
<BulkUploadResultPopup
  open={bulkUploadPopupOpen}
  onClose={() => {
    setBulkUploadPopupOpen(false);
    setBulkUploadResult(null);
  }}
  data={bulkUploadResult}
  type="questions"
/>

{/* Guidelines Modal */}
{guidelinesModalOpen && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    backdropFilter: 'blur(4px)'
  }} onClick={() => setGuidelinesModalOpen(false)}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '16px',
      maxWidth: '1000px',
      width: '90%',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden'
    }} onClick={e => e.stopPropagation()}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '24px 32px',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '700', letterSpacing: '-0.5px' }}>📋 Assessment Guidelines</h2>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
            {isAdmin ? 'Department-specific guidelines' : `Guidelines for ${managerDept}`}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {isAdmin && (
            <select
              value={selectedGuidelinesDept}
              onChange={async (e) => {
                setSelectedGuidelinesDept(e.target.value);
                await fetchGuidelines(e.target.value);
              }}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#1f2937'
              }}
            >
              <option value="">Select Department</option>
              {departments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setGuidelinesModalOpen(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              fontSize: '28px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
        </div>
      </div>
      
      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '32px' }}>
        {guidelinesLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #e2e8f0',
              borderTop: '3px solid #667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }}></div>
            <p style={{ color: '#64748b', fontSize: '14px' }}>Loading guidelines...</p>
          </div>
        ) : guidelines.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px',
              opacity: 0.5
            }}>📋</div>
            <h3 style={{ color: '#64748b', fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>
              No guidelines uploaded for this department
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
              Upload guidelines using the bulk upload template to see them here.
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '700', color: '#1e293b', width: '70px', borderTopLeftRadius: '8px', borderBottom: '2px solid #cbd5e1' }}>S.No</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '700', color: '#1e293b', width: '200px', borderBottom: '2px solid #cbd5e1' }}>Section</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '700', color: '#1e293b', borderTopRightRadius: '8px', borderBottom: '2px solid #cbd5e1' }}>Guideline / Description</th>
              </tr>
            </thead>
            <tbody>
              {guidelines.map((item, idx) => (
                <tr key={idx} style={{ transition: 'background 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '16px', color: '#64748b', verticalAlign: 'top', fontWeight: '600', borderBottom: '1px solid #e2e8f0' }}>{item.sno}</td>
                  <td style={{ padding: '16px', color: '#0f172a', fontWeight: '600', verticalAlign: 'top', borderBottom: '1px solid #e2e8f0' }}>{item.section}</td>
                  <td style={{ padding: '16px', color: '#475569', lineHeight: '1.7', whiteSpace: 'pre-line', verticalAlign: 'top', borderBottom: '1px solid #e2e8f0' }}>{item.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      {/* Footer */}
      <div style={{ padding: '20px 32px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', background: '#f8fafc' }}>
        <button
          onClick={() => setGuidelinesModalOpen(false)}
          style={{
            padding: '12px 32px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '15px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
          }}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
  
}

const QuestionModal = ({ open, onClose, data }) => {
  if (!open || !data) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>{data.role} – {data.department}</h2>

        {Object.entries(data.groupedQuestions).map(([catId, catData]) => (
          <div key={catId} className="category-section">
            <h3 data-count={`${catData.questions.length} Question${catData.questions.length > 1 ? 's' : ''}`}
                title={catData.description}
            >
              {catData.name}
            </h3>
            <ul className="questions-list">
              {catData.questions.map((q, idx) => (
                <li key={q.ID} className="question-item">
                  <div className="question-text">
                    {q.Question}
                  </div>
                  {q.Description && (
                    <div className="question-description" style={{ 
                      fontSize: '14px', 
                      color: '#6b7280', 
                      marginTop: '8px',
                      fontStyle: 'italic',
                      lineHeight: '1.4'
                    }}>
                      {q.Description}
                    </div>
                  )}
                  <div className="question-meta">
                    <span>Created: {q.CreatedAt.split(' ')[0]}</span>
                    <span className="weight-badge">Weight: {q.Weightage}%</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};


export default ManageAssessments;